import { showToast } from "@components/toast/toastSlice";
import { ToastType } from "@emerald-react/toast";
import { useDispatch } from "react-redux";
import { ConfirmationOptions, useConfirmation } from "./useConfirmation";

/**
 * Higher-level utility hook that provides simplified, opinionated methods
 * for common confirmation patterns
 */
export const useSimpleConfirmation = () => {
  const confirmation = useConfirmation();
  const dispatch = useDispatch();

  const showToastMessage = (
    message: string,
    type: ToastType = ToastType.ERROR,
  ) => {
    const title = type === ToastType.SUCCESS ? "Success" : "Error";
    dispatch(
      showToast({
        type,
        title,
        message,
      }),
    );
  };

  return {
    // Quick delete with consistent styling
    quickDelete: async (options: Partial<ConfirmationOptions> = {}) =>
      await confirmation.confirmDelete({
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        ...options,
      }),

    // Quick success with auto-close
    quickSuccess: async (message: string) =>
      await confirmation.showSuccess("Success!", message, {
        timer: 2000,
      }),

    // Quick error with consistent styling
    quickError: async (message: string) =>
      await confirmation.showError("Error!", message, {
        confirmButtonColor: "#dc3545",
      }),

    // Quick publish confirmation
    quickPublish: async (itemName: string = "item") =>
      await confirmation.confirmAction(
        `Publish ${itemName}?`,
        `This will make your ${itemName} publicly available.`,
        {
          confirmButtonText: "🚀 Publish",
          confirmButtonColor: "#28a745",
          icon: "question",
        },
      ),

    // Quick unsaved changes
    quickUnsaved: async () =>
      await confirmation.confirmUnsavedChanges({
        title: "Discard Changes?",
        confirmButtonColor: "#dc3545",
      }),

    // Async operation with loading
    withLoading: async <T>(
      operation: () => Promise<T>,
      options: {
        loadingText?: string;
        successText?: string;
        errorText?: string;
      } = {},
    ): Promise<T | null> => {
      const { successText, errorText = "Please try again." } = options;

      try {
        const result = await operation();
        confirmation.closeDialog();

        if (successText) {
          showToastMessage(successText, ToastType.SUCCESS);
        }

        return result;
      } catch (error) {
        confirmation.closeDialog();
        showToastMessage(error instanceof Error ? error.message : errorText);
        return null;
      }
    },

    // Expose original hook for advanced usage
    ...confirmation,
  };
};

export default useSimpleConfirmation;
